import './interactive-map.scss';
import { Loader } from 'google-maps';
import { style } from './map-theme';
import InfoWindow = google.maps.InfoWindow;
import Marker = google.maps.Marker;

export interface MapCardProps {
  id: string | number;
  region: 'eu' | 'usa' | 'asia';
  title: string;
  description: string;
  location: google.maps.LatLngLiteral;
  image: {
    src: string;
  };
  link?: {
    title: string;
    url: string;
  };
  active?: boolean | undefined;
}

interface MarkerProps extends google.maps.Marker {
  id: string | number;
}

interface ElementInterface {
  items: MapCardProps[];
  markers: google.maps.Marker[];
  map?: google.maps.Map<HTMLElement>;
}

const selectors: Record<'block' | 'map' | 'card', string> = {
  block: '[data-map]',
  map: '[data-map-instance]',
  card: '[data-map-card]',
};

const elements: ElementInterface = {
  items: [],
  markers: [],
  map: undefined,
};

const classes: Record<'isActive', string> = {
  isActive: 'active',
};

const init = () => {
  const block = document.querySelector(selectors.block);

  if (!block) return;

  const map = block.querySelector(selectors.map) as HTMLElement;
  // @ts-ignore
  const items = window.BLOCK_MAP;

  elements.items = items;

  initGoogleMap(map);

  Array.from(block.querySelectorAll(selectors.card), (card) => {
    card.addEventListener('mouseenter', initMapCardHover);
    // card.addEventListener('mouseleave', initMapCardLeave);
  });

  window.addEventListener('resize', () => {
    google.maps.event.trigger(elements.map, 'resize');
  });
};

const initMapCardHover = (e: Event) => {
  const target = e.currentTarget as HTMLElement;
  const id = target.getAttribute('data-map-card');
  const pin = elements.items.find((i) => Number(i.id) === Number(id));

  if (pin) {
    getMapBounds(
      elements.items.map((i) => ({ ...i, active: Number(i.id) === Number(id) }))
    );
    elements.map?.panTo(pin.location);
  }

  // getMapBounds(
  //   elements.items
  //     .filter((item) => Number(item.id) === Number(id))
  //     .map((i) => ({ ...i, active: true }))
  // );
  // getMapBounds(elements.items.map((i) => ({ ...i, active: i.id === id })));
};

const initMapCardLeave = (e: Event) => getMapBounds(elements.items);

const getMapBounds = (items: MapCardProps[]) => {
  if (!elements.map) return;

  let activeInfoWindow: InfoWindow | undefined;
  let activeMarker: Marker | undefined;

  const bounds = new google.maps.LatLngBounds();
  const icon = (active = false) => ({
    url: 'data:image/svg+xml;charset=UTF-8;base64,' + btoa(pin(active)),
    scaledSize: new google.maps.Size(34, 42),
  });

  elements.markers.forEach((marker) => marker.setMap(null));

  for (let i = 0; i < items.length; i++) {
    const marker = new google.maps.Marker({
      position: new google.maps.LatLng(
        items[i].location.lat,
        items[i].location.lng
      ),
      map: elements.map,
      icon: icon(items[i].active),
    });
    marker.set('id', items[i].id);
    const position = marker.getPosition();

    //@ts-ignore
    elements.markers.push(marker);

    if (position) {
      bounds.extend(position);
    }

    const infoWindow = new google.maps.InfoWindow({
      content: renderCard(items[i]),
    });

    google.maps.event.addListener(marker, 'click', function () {
      if (activeInfoWindow && activeMarker) {
        activeInfoWindow.close();
        activeMarker.setIcon(icon(false));
      }

      marker.setIcon(icon(true));
      infoWindow.open(elements.map, marker);
      activeInfoWindow = infoWindow;
      activeMarker = marker;
    });

    google.maps.event.addListener(elements.map, 'click', function () {
      activeInfoWindow = undefined;
      activeMarker?.setIcon(icon(false));

      infoWindow.close();
      marker.setIcon(icon(false));
      if (elements.map) {
        elements.map.fitBounds(bounds);
      }
    });
  }

  if (elements.map) {
    elements.map.fitBounds(bounds);

    if (items.length === 1) {
      elements.map.setZoom(7);
    }
  }
};

const initGoogleMap = async (instance: HTMLElement) => {
  const loader = new Loader('AIzaSyCznCFarr2M8jkHRjoGKQORJ9CTLSFI2e8', {});
  const google = await loader.load();

  elements.map = new google.maps.Map(instance, {
    styles: style,
    disableDefaultUI: true,
  });

  getMapBounds(elements.items);
};

document.addEventListener(
  'DOMContentLoaded',
  async function () {
    init();
  },
  {
    once: true,
  }
);

const renderCard = (data: MapCardProps) => `
<div class="c-office c-office--type-pin c-office--region-${data.region}">
  ${
    data.image.src
      ? `
    <picture>
      <img src="${data.image.src}" alt="image" class="c-office__thumb">
    </picture>
  `
      : ''
  }
  <div class="c-office__info">
    ${
      data.title
        ? `
    <span class="body-400">${data.title}</span>`
        : ''
    }
    ${
      data.description
        ? `
      <span class="body-300">${data.description}</span>
    `
        : ''
    }
    ${
      data.link?.title
        ? `
      <div>
        <a class="button button--variant-link button--color-light " href="${data.link?.url}" target="_self">
          <span class="body-300 lg:body-400 body-bold">${data.link?.title}</span>
        </a>
      </div>
    `
        : ''
    }
  </div>
</div>`;

const template = [
  '<?xml version="1.0"?>',
  '<svg width="37" height="46" viewBox="0 0 37 46" fill="none" xmlns="http://www.w3.org/2000/svg">',
  '<path d="M30.6573 6.33672L30.6568 6.33624C27.0687 3.06591 22.9299 1.42578 18.25 1.42578C13.5711 1.42578 9.42119 3.06531 5.87161 6.33769C2.28508 9.63482 0.5 14.0254 0.5 19.4758C0.5 23.5054 1.98919 27.5452 4.89396 31.5876L4.89445 31.5882C7.79817 35.615 12.1526 40.0454 17.9299 44.8599L18.2497 45.1264L18.5698 44.8602C24.3571 40.0458 28.7214 35.6155 31.6351 31.5889C34.5606 27.5459 36.05 23.5056 36.05 19.4758C36.05 14.0252 34.2648 9.63406 30.6573 6.33672ZM15.1441 16.0946L15.1417 16.097C14.3174 16.9439 13.9 17.9905 13.9 19.2158C13.9 20.4412 14.3179 21.4921 15.1688 22.3317L15.1689 22.3317C16.0067 23.1582 17.0546 23.5758 18.27 23.5758C19.4862 23.5758 20.5348 23.1575 21.3824 22.3105C22.2321 21.4716 22.65 20.4214 22.65 19.2058C22.65 17.989 22.2314 16.94 21.3836 16.0922C20.5353 15.244 19.4861 14.8258 18.26 14.8258C17.0238 14.8258 15.9832 15.2441 15.1441 16.0946ZM27.3188 27.7914C25.3541 30.6281 22.3386 33.8962 18.2556 37.5971C14.2746 33.8678 11.2735 30.5881 9.25733 27.7558C7.19907 24.8645 6.2 22.1039 6.2 19.4758C6.2 15.8451 7.37621 12.8981 9.69118 10.6117C12.024 8.31757 14.8663 7.17578 18.26 7.17578C21.6541 7.17578 24.4962 8.31785 26.8187 10.6115C29.133 12.8972 30.3 15.844 30.3 19.4758C30.3 22.1078 29.328 24.8803 27.3188 27.7914Z" fill="#C7D8C5" stroke="#161616"/>',
  '<path d="M27.1702 10.2558C24.7502 7.86578 21.7802 6.67578 18.2602 6.67578C14.7402 6.67578 11.7702 7.86578 9.3402 10.2558C6.9202 12.6458 5.7002 15.7258 5.7002 19.4758C5.7002 22.2358 6.7502 25.0958 8.8502 28.0458C10.9502 30.9958 14.0902 34.4058 18.2502 38.2758C22.5202 34.4358 25.6802 31.0358 27.7302 28.0758C29.7802 25.1058 30.8002 22.2358 30.8002 19.4758C30.8002 15.7258 29.5902 12.6458 27.1702 10.2558ZM21.0302 21.9558C20.2802 22.7058 19.3602 23.0758 18.2702 23.0758C17.1802 23.0758 16.2602 22.7058 15.5202 21.9758C14.7702 21.2358 14.4002 20.3258 14.4002 19.2158C14.4002 18.1058 14.7702 17.1958 15.5002 16.4458C16.2402 15.6958 17.1502 15.3258 18.2602 15.3258C19.3702 15.3258 20.2802 15.6958 21.0302 16.4458C21.7802 17.1958 22.1502 18.1158 22.1502 19.2058C22.1502 20.2958 21.7802 21.2158 21.0302 21.9558Z" fill="#161616"/>',
  '</svg>',
].join('\n');
const pin = (active = false) =>
  active ? template.replace('#C7D8C5', '#B3FC4F') : template;
